/**
 * Global Loading Manager
 * 
 * Centralizes loading state to prevent double-loading indicators.
 * Shows loading spinner only if transition takes > 200ms, hides with 150ms fade.
 */

import { useEffect, useState, useCallback, useRef } from 'react';

export interface LoadingState {
  isLoading: boolean;
  message?: string;
}

class GlobalLoadingManager {
  private subscribers: Set<(state: LoadingState) => void> = new Set();
  private pendingTimeout: NodeJS.Timeout | null = null;
  private hideTimeout: NodeJS.Timeout | null = null;
  private currentState: LoadingState = { isLoading: false };
  private showDelay = 200; // Show spinner only if loading > 200ms
  private hideDelay = 150; // Fade out delay

  /**
   * Subscribe to loading state changes
   */
  subscribe(callback: (state: LoadingState) => void): () => void {
    this.subscribers.add(callback);
    // Immediately notify with current state
    callback(this.currentState);
    
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify all subscribers of state change
   */
  private notify(state: LoadingState): void {
    this.currentState = state;
    this.subscribers.forEach(callback => callback(state));
  }

  /**
   * Show loading indicator (debounced)
   */
  show(message?: string): void {
    // Clear any pending hide
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    // Clear any pending show
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
    }

    // Show after delay
    this.pendingTimeout = setTimeout(() => {
      this.notify({ isLoading: true, message });
      this.pendingTimeout = null;
    }, this.showDelay);
  }

  /**
   * Hide loading indicator (with fade delay)
   */
  hide(): void {
    // Clear any pending show
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }

    // Clear any pending hide
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }

    // Hide after fade delay
    this.hideTimeout = setTimeout(() => {
      this.notify({ isLoading: false });
      this.hideTimeout = null;
    }, this.hideDelay);
  }

  /**
   * Force immediate hide (for critical transitions)
   */
  forceHide(): void {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    this.notify({ isLoading: false });
  }
}

// Singleton instance
export const globalLoadingManager = new GlobalLoadingManager();

/**
 * Hook to use global loading state
 */
export function useGlobalLoading(): LoadingState {
  const [state, setState] = useState<LoadingState>({ isLoading: false });

  useEffect(() => {
    const unsubscribe = globalLoadingManager.subscribe(setState);
    return unsubscribe;
  }, []);

  return state;
}

/**
 * Hook to control global loading
 */
export function useGlobalLoadingControl() {
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const show = useCallback((message?: string) => {
    globalLoadingManager.show(message);
  }, []);

  const hide = useCallback(() => {
    globalLoadingManager.hide();
  }, []);

  const forceHide = useCallback(() => {
    globalLoadingManager.forceHide();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      globalLoadingManager.forceHide();
    };
  }, []);

  return { show, hide, forceHide };
}

